/********************************/
/*          About vuex          */
/********************************/
// The MIT License (MIT)
//
// Copyright (c) 2015-present Evan You
//
// Permission is hereby granted, free of charge, to any person obtaining a copy
// of this software and associated documentation files (the "Software"), to deal
// in the Software without restriction, including without limitation the rights
// to use, copy, modify, merge, publish, distribute, sublicense, and/or sell
// copies of the Software, and to permit persons to whom the Software is
// furnished to do so, subject to the following conditions:
//
// The above copyright notice and this permission notice shall be included in
// all copies or substantial portions of the Software.
//
// THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR
// IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY,
// FITNESS FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE
// AUTHORS OR COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER
// LIABILITY, WHETHER IN AN ACTION OF CONTRACT, TORT OR OTHERWISE, ARISING FROM,
// OUT OF OR IN CONNECTION WITH THE SOFTWARE OR THE USE OR OTHER DEALINGS IN
// THE SOFTWARE.

import error from '../../../../common/nodejs/errors';
import { mapState, mapActions } from 'vuex';
import { apiErrorDefaultAction } from '../../../../share/util/api/errorAction';
import session from '../../../../share/session';
import individualDiagnose from '../../../../share/util/individualDiagnose';
import individualWorkSupportUtil from '../../../../share/util/individualWorkSupportUtil';
import { REQUEST_STATUS_COMPLETED, REQUEST_STATUS_FAILED } from '../../../../share/util/api/polling';

/** 各フェーズ定義 */
const PHASE = {
  CONFIRM: "confirm",
  INSTRUCTION: "instruction",
  EXECUTE: "execute",
  SUCCESS: "success",
  FAILED: "failed",
  ERROR: "error"
};

/** API type定義 */
const API_TYPE = {
  STANDBY: 'standby',
  EXECUTE: 'execute',
  TERMINATE: 'terminate',
  DISPOSE: 'dispose'
};

/** 現在セッション */
const SESSION_PROPERTY = {
  DEFAULT: 0,
  NON_DEFAULT: 1
};

export default {
  name: "requirement147",
  data() {
    return {
      phase: PHASE.CONFIRM,
      isLoading: false,
      modal_requirement_147: false,
      sessionProperty: SESSION_PROPERTY.DEFAULT,
      errorMessage: ""
    };
  },

  props: {
    i18nWsKey: { type: String, default: "", required: true },
    workSupportId: { type: Number, default: "", required: true }
  },

  computed: {
    ...mapState({
      netAppId: state => state.netAppConnector.pairingAppId
    }),
    /**
     * ダイアログのヘッダー色
     * @returns {string} ヘッダー色
     */
    headerColor() {
      switch (this.phase) {
        case PHASE.FAILED:
        case PHASE.ERROR:
          return "danger";
        case PHASE.SUCCESS:
          return "success";
        case PHASE.CONFIRM:
        case PHASE.INSTRUCTION:
        case PHASE.EXECUTE:
        default:
          return "secondary";
      }
    },

    /**
     * 表示ヘッダー文字列判定
     * @returns {string} ヘッダー文字列
     */
    headerTitle() {
      const ConfirmTitle = 'message.common_title_confirmation';
      const ExecuteTitle = 'individual.common_title_execution';
      const SuccessTitle = 'message.common_title_success';
      const ErrorTitle = 'message.common_title_error';

      const titleMap = {
        [PHASE.CONFIRM]: ConfirmTitle,
        [PHASE.INSTRUCTION]: ConfirmTitle,
        [PHASE.EXECUTE]: ExecuteTitle,
        [PHASE.SUCCESS]: SuccessTitle,
        [PHASE.FAILED]: ErrorTitle,
        [PHASE.ERROR]: ErrorTitle,
      };

      return this.$t(titleMap[this.phase]);
    },

    /**
     * 作業サポート表示メッセージ判定
     * @returns {string} メッセージ
     */
    workSupportMessage() {
      const messageMap = {
        [PHASE.CONFIRM]: this.$t(`${this.i18nWsKey}.REQ_WS_147_TXT_1`),
        [PHASE.INSTRUCTION]: this.$t(`${this.i18nWsKey}.REQ_WS_147_TXT_2`),
        [PHASE.EXECUTE]: this.$t(`${this.i18nWsKey}.REQ_WS_147_TXT_3`),
        [PHASE.SUCCESS]: this.$t(`${this.i18nWsKey}.REQ_WS_147_TXT_4`),
        [PHASE.FAILED]: this.errorMessage,
        [PHASE.ERROR]: this.$t(`${this.i18nWsKey}.REQ_WS_147_TXT_6`),
      };

      return messageMap[this.phase];
    },

    /**
     * [OK]ボタンの表示判定
     * @returns {boolean} trueなら表示
     */
    showButtonOk() {
      const showCancelButtonMap = {
        [PHASE.CONFIRM]: true,
        [PHASE.INSTRUCTION]: true,
        [PHASE.EXECUTE]: false,
        [PHASE.SUCCESS]: true,
        [PHASE.FAILED]: true,
        [PHASE.ERROR]: true,
      };
      let result = showCancelButtonMap[this.phase];
      if (!result) {
        // 合致しない場合は false とする。
        result = false;
      }
      return result;
    },

    /**
     * [キャンセル]ボタンの表示判定
     * @returns {boolean} trueなら表示
     */
    showButtonCancel() {
      const showCancelButtonMap = {
        [PHASE.CONFIRM]: true,
        [PHASE.INSTRUCTION]: true,
        [PHASE.EXECUTE]: false,
        [PHASE.SUCCESS]: false,
        [PHASE.FAILED]: false,
        [PHASE.ERROR]: false,
      };
      let result = showCancelButtonMap[this.phase];
      if (!result) {
        // 合致しない場合は false とする。
        result = false;
      }
      return result;
    }
  },
  methods: {
    ...mapActions("auth", ["logout", "resetSession"]),

    /** 初期化処理 */
    init() {
      this.sessionProperty = SESSION_PROPERTY.DEFAULT;
      this.modal_requirement_147 = true;
    },

    /** Wizardを閉じる */
    closeWizard() {
      this.$emit('close-wizard');
      this.modal_requirement_147 = false;
    },

    /**
     * 共通の[キャンセル]ボタンクリック処理
     */
    onCancelClicked() {
      this.closeWizard();
    },

    /**
     * 共通の[OK]ボタンクリック処理
     */
    async onOkClicked() {
      switch (this.phase) {
        case PHASE.CONFIRM:
          this.clickConfirmOk();
          break;
        case PHASE.INSTRUCTION:
          await this.clickInstructionOk();
          break;
        case PHASE.SUCCESS:
          await this.clickSucceedOk();
          break;
        case PHASE.ERROR:
        case PHASE.FAILED:
          this.closeWizard();
          break;
        default:
          // noop
          break;
      }
    },

    /**
     * 実行確認画面のOKクリック
     */
    clickConfirmOk() {
      // IG ON指示画面に遷移
      this.phase = PHASE.INSTRUCTION;
    },

    /**
     * IG ON指示画面のOKクリック
     */
    async clickInstructionOk() {
      // NonDefaultSession遷移 + ルーチン実行処理
      this.phase = PHASE.EXECUTE;
      await this.standby();
    },

    /**
     * 成功画面のOKクリック
     */
    async clickSucceedOk() {
      // DefaultSession遷移 + Dispose処理
      await this.terminate();
      this.closeWizard();
    },

    /** 終了処理 */
    async exitProcess() {
      if (this.sessionProperty === SESSION_PROPERTY.NON_DEFAULT) {
        // NonDefaultSession遷移済みの場合、DefaultSession遷移 + Dispose処理
        await this.terminate();
      } else {
        // Dispose処理
        await this.dispose();
      }
    },

    /**
     * API 通信で共通バリデーションでエラーが発生した際のコールバック
     * @param {object} result バリデーション結果
     */
    validateErrorResult(result) {
      apiErrorDefaultAction(
        this,
        result,
        // ログアウト処理のコールバック
        (self) => { self; this.logout(this); },
        // セッションリセット処理のコールバック
        (self) => { self; this.resetSession(this); },
        // その他エラー発生のコールバック
        // 呼び元でエラー処理を実行する
        (result) => { result; },
      );
    },

    /**
     * API 実行処理
     * @param {string} type API実行種別
     * @returns {object?} API実行結果
     */
    async executeApi(type) {
      const systemId = individualDiagnose.getCurrentSystemId(this);
      const token = session.getToken();
      // ポーリング
      const response = await individualWorkSupportUtil.pollingWspApi(
        this,
        this.netAppId,
        systemId,
        this.workSupportId,
        {
          id_token: token.id_token,
          type: type
        },
        // エラー発生のコールバックを実装する。
        // ここでは利用していないが、引数の errorType にはあらかじめ用意された定数文字列、もしくは独自定義した文字列が返ってくる。
        // 必要に応じて、errorType に応じてメッセージを変えるといったことも可能。
        (errorType) => { errorType; },
        // POST API 結果が異常の場合（handler.validate でエラーが発生した場合）のコールバックを定義する（validateErrorResult の中は同様のため、省略する）
        (result) => { this.validateErrorResult(result); },
        // GET API 結果が異常の場合（handler.validate でエラーが発生した場合）のコールバックを定義する（validateErrorResult の中は同様のため、省略する）
        (result) => { this.validateErrorResult(result); },
      );
      return response;
    },

    /** NonDefaultSession遷移 */
    async standby() {
      try {
        this.isLoading = true;
        const response = await this.executeApi(API_TYPE.STANDBY);
        // responseが存在しない場合、通信エラーに遷移
        if (!response) {
          await this.showUnexpectedError();
          return;
        }

        switch (response.data.status) {
          case REQUEST_STATUS_COMPLETED:
            // NonDefaultSession遷移成功
            this.sessionProperty = SESSION_PROPERTY.NON_DEFAULT;
            // 正常終了 ルーチン実行処理
            await this.execute();
            break;
          case REQUEST_STATUS_FAILED:
            // 異常終了
            if (response.data.async_code === error.WSP_147_EXECUTE_ERROR) {
              // NRC
              // NRCの場合、受信したSIDとNRCを表示
              this.errorMessage = this.$t(`${this.i18nWsKey}.REQ_WS_147_TXT_5`)
                .replace(/\$SID\$/g, response.data.user_data.sid)
                .replace(/\$NRC\$/g, response.data.user_data.nrc);
              await this.exitProcess();
              this.phase = PHASE.FAILED;
            } else {
              // NRC以外
              await this.showUnexpectedError();
            }
            break;
          default:
            await this.showUnexpectedError();
            break;
        }
      } finally {
        this.isLoading = false;
      }
    },

    /** 実行 */
    async execute() {
      const response = await this.executeApi(API_TYPE.EXECUTE);
      // responseが存在しない場合、通信エラーに遷移
      if (!response) {
        await this.showUnexpectedError();
        return;
      }

      switch (response.data.status) {
        case REQUEST_STATUS_COMPLETED:
          // 正常終了：成功表示
          this.phase = PHASE.SUCCESS;
          break;
        case REQUEST_STATUS_FAILED:
          // 異常終了
          if (response.data.async_code === error.WSP_147_EXECUTE_ERROR) {
            // NRC
            // NRCの場合、受信したSIDとNRCを表示
            this.errorMessage = this.$t(`${this.i18nWsKey}.REQ_WS_147_TXT_5`)
              .replace(/\$SID\$/g, response.data.user_data.sid)
              .replace(/\$NRC\$/g, response.data.user_data.nrc);
            await this.exitProcess();
            this.phase = PHASE.FAILED;
          } else {
            // NRC以外
            // 通信エラー
            await this.showUnexpectedError();
          }
          break;
        default:
          await this.showUnexpectedError();
          break;
      }
    },

    /** DefaultSession遷移 */
    async terminate() {
      try {
        this.isLoading = true;
        await this.executeApi(API_TYPE.TERMINATE);
        // 実行結果に関わらずDispose実行
        await this.dispose();
      } finally {
        this.isLoading = false;
      }
    },

    /**
     * Dispose実行
     */
    async dispose() {
      try {
        this.isLoading = true;
        await this.executeApi(API_TYPE.DISPOSE);
      } finally {
        this.isLoading = false;
      }
    },

    /** エラー発生時の表示 */
    async showUnexpectedError() {
      await this.exitProcess();
      // 通信エラー表示
      this.phase = PHASE.ERROR;
    }
  },

  /** DOM作成後実行処理 */
  mounted() {
    this.init();
  }
};
